import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkle, ArrowLeft } from "@phosphor-icons/react";

export default function ParseJobDialog({ trigger, onCreated }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [parsed, setParsed] = useState(null);

  const reset = () => { setUrl(""); setText(""); setParsed(null); };

  const parse = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/jobs/parse", { url, text });
      setParsed(data.parsed);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const confirmCreate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/jobs", parsed);
      toast.success("Job added and analysed against your profile");
      setOpen(false);
      reset();
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const upd = (k, v) => setParsed((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {parsed ? "Review before adding" : "Add a job from a link or text"}
          </DialogTitle>
        </DialogHeader>

        {!parsed && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Paste a job URL or the full advert text. We extract structured fields — nothing is invented, and unknown fields stay empty. You review before it is saved.</p>
            <div>
              <Label>Job URL (optional)</Label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="mt-1.5" data-testid="parse-url-input" />
            </div>
            <div>
              <Label>Or paste job text</Label>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder="Paste the full job advertisement…" className="mt-1.5" data-testid="parse-text-input" />
            </div>
            <DialogFooter>
              <Button onClick={parse} disabled={loading || (!url && !text)} data-testid="parse-submit-btn" className="bg-slate-900 hover:bg-slate-800 gap-2">
                <Sparkle weight="fill" /> {loading ? "Extracting…" : "Extract fields"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {parsed && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Confirm the extracted fields. Correct anything that's wrong before saving — this becomes trusted structured data.</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Title" value={parsed.title} onChange={(v) => upd("title", v)} />
              <Field label="Employer" value={parsed.employer} onChange={(v) => upd("employer", v)} />
              <Field label="Country" value={parsed.country} onChange={(v) => upd("country", v)} />
              <Field label="City" value={parsed.city} onChange={(v) => upd("city", v)} />
              <Field label="Work arrangement" value={parsed.work_arrangement} onChange={(v) => upd("work_arrangement", v)} />
              <Field label="Employment type" value={parsed.employment_type} onChange={(v) => upd("employment_type", v)} />
              <Field label="Required residence country" value={parsed.required_residence_country} onChange={(v) => upd("required_residence_country", v)} />
              <Field label="Work authorization country" value={parsed.work_authorization_country} onChange={(v) => upd("work_authorization_country", v)} />
            </div>
            <div className="text-xs text-slate-500 rounded-md bg-slate-50 border border-slate-200 p-3 space-y-1">
              <p><strong>Mandatory:</strong> {(parsed.mandatory_qualifications || []).join(", ") || "—"}</p>
              <p><strong>Preferred:</strong> {(parsed.preferred_qualifications || []).join(", ") || "—"}</p>
              <p><strong>Remote from:</strong> {(parsed.remote_countries || []).join(", ") || "Not specified"}</p>
              <p><strong>Source:</strong> {parsed.source_url || "—"}</p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setParsed(null)} className="gap-1"><ArrowLeft /> Back</Button>
              <Button onClick={confirmCreate} disabled={loading} data-testid="parse-confirm-btn" className="bg-slate-900 hover:bg-slate-800">
                {loading ? "Saving…" : "Confirm & add job"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9" placeholder="Unknown" />
    </div>
  );
}
